import {
  UpdatePresentationFamilyOverviewBundleConfigurationInput,
  UpdatePresentationFamilyOverviewMemberConfigurationInput,
} from '@/types/schema';

import { NEW_ROW_ID_SENTINEL } from '../../ClientPresentationDesigner.constants';
import {
  isEmptyKeyedListMutationOperations,
  mapKeyedListMutationOperations,
} from '../diff/mapKeyedListMutationOperations';
import {
  BundleConfigurationPayload,
  DiffBundleConfigurationArgs,
} from '../presentationSave.types';

function serverRowId(row: { id: string }): string | null {
  return row.id.startsWith(NEW_ROW_ID_SENTINEL) ? null : row.id;
}

function positionsById(rows: { id: string }[]): Map<string, number> {
  return new Map(rows.map((row, index) => [row.id, index]));
}

function diffDisplayName(
  previous: string | null,
  next: string | null
): { displayName?: string; clearDisplayName?: boolean } | null {
  if (previous === next) return null;
  return next ? { displayName: next } : { clearDisplayName: true };
}

export function diffFamilyOverviewBundleConfiguration({
  previous,
  next,
  context,
}: DiffBundleConfigurationArgs): BundleConfigurationPayload | null {
  const before = previous.familyOverviewBundleConfiguration;
  const after = next.familyOverviewBundleConfiguration;
  if (!before || !after?.id) return null;

  const update: UpdatePresentationFamilyOverviewBundleConfigurationInput = {
    ...diffDisplayName(before.displayName, after.displayName),
  };
  const wasAt = positionsById(before.members);
  const rows = mapKeyedListMutationOperations({
    previousItems: before.members,
    currentItems: after.members,
    getId: serverRowId,
    toCreate: (row, sortOrder) => ({
      create: {
        householdID: context.householdId,
        name: row.name,
        relationship: row.relationship,
        description: row.description,
        sortOrder,
      },
    }),
    toUpdate: ({ previous: was, next: is, sortOrder }) => {
      const row: UpdatePresentationFamilyOverviewMemberConfigurationInput = {};
      if (was.name !== is.name) row.name = is.name;
      if (was.relationship !== is.relationship) {
        row.relationship = is.relationship;
      }
      if (was.description !== is.description) {
        row.description = is.description;
      }
      if (wasAt.get(is.id) !== sortOrder) row.sortOrder = sortOrder;
      return Object.keys(row).length > 0 ? { id: is.id, update: row } : null;
    },
  });

  if (rows.removeIDs.length > 0) update.removeMemberIDs = rows.removeIDs;
  if (
    Object.keys(update).length === 0 &&
    isEmptyKeyedListMutationOperations(rows)
  ) {
    return null;
  }

  return {
    updateFamilyOverviewBundleConfiguration: {
      id: after.id,
      update,
      ...(rows.creates.length > 0 ? { withMembers: rows.creates } : {}),
      ...(rows.updates.length > 0 ? { updateMembers: rows.updates } : {}),
    },
  };
}
